/*
 * Phone Controller
 * Initializes intl-tel-input for international phone number formatting
 *
 * Usage:
 * <div data-controller="phone">
 *   <input type="tel"
 *          class="form-control"
 *          data-phone-target="input">
 * </div>
 *
 * Works in tandem with mask_controller for US number formatting.
 * On submit, a non-US number is sent with its country's dial code (+64...)
 * so the server can parse it and a re-render restores the selected country.
 */

import { Controller } from "@hotwired/stimulus"
import intlTelInput from "intl-tel-input"

export default class extends Controller {
  static targets = ["input"]

  connect() {
    // Find the input element - either a target or the first input in the element
    const input = this.hasInputTarget ? this.inputTarget : this.element.querySelector("input")

    if (input) {
      // Ensure input has a value attribute (intl-tel-input requires it)
      if (input.value === undefined || input.value === null) {
        input.value = ""
      }

      this.input = input
      this.iti = intlTelInput(input, {
        initialCountry: "us",
        countryOrder: ["us", "ca", "mx"],
        separateDialCode: true
      })

      this.addDialCode = this.addDialCode.bind(this)
      input.form?.addEventListener("submit", this.addDialCode)
    }
  }

  disconnect() {
    this.input?.form?.removeEventListener("submit", this.addDialCode)
    if (this.iti) {
      this.iti.destroy()
    }
  }

  // intl-tel-input's utils aren't loaded, so getNumber() returns "" and can't be used here.
  // US, Canada and the Caribbean share +1: the server adds it to 10 digits, so only a
  // typed leading 1 is dropped (mask_controller already does this for the US).
  addDialCode() {
    const value = this.input.value.trim()
    const { dialCode } = this.iti.getSelectedCountryData()
    if (!value || value.startsWith("+") || !dialCode) return

    const digits = value.replace(/\D/g, "")
    if (dialCode !== "1") {
      this.input.value = `+${dialCode}${digits}`
    } else if (digits.length === 11 && digits.startsWith("1")) {
      this.input.value = digits.slice(1)
    }
  }
}
